import {
  ADDRESS_TRUNCATION,
  BASESCAN,
  CURRENCY,
  DATE_FORMATS,
  NUMBER_DISPLAY,
} from '@/constants'

export interface DateFormatOptions {
  year?: '2-digit' | 'numeric'
  month?: '2-digit' | 'long' | 'numeric' | 'short'
  day?: '2-digit' | 'numeric'
  hour?: '2-digit' | 'numeric'
  minute?: '2-digit' | 'numeric'
}

export function formatDate(
  date: Date | number | string,
  options: DateFormatOptions = DATE_FORMATS.DEFAULT,
  locale: string = DATE_FORMATS.LOCALE,
): string {
  if (date == null) {
    return ''
  }
  if (typeof date === 'string' && date === '') {
    return ''
  }
  if (typeof date === 'number' && date === 0) {
    return ''
  }

  try {
    // Date-only strings (YYYY-MM-DD) are parsed as UTC midnight by the JS spec.
    // Append T00:00:00 (no Z) so they parse as local midnight instead,
    // preventing off-by-one day shifts in negative UTC offsets.
    const normalized = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
      ? `${date}T00:00:00`
      : date
    const dateObj = normalized instanceof Date ? normalized : new Date(normalized)

    if (Number.isNaN(dateObj.getTime())) {
      return ''
    }

    return dateObj.toLocaleDateString(locale, options)
  }
  catch {
    return ''
  }
}

export function formatDateCompact(date: Date | number | string): string {
  return formatDate(date, DATE_FORMATS.COMPACT)
}

export function formatDateFull(date: Date | number | string): string {
  return formatDate(date, DATE_FORMATS.FULL)
}

/** Month, day and year as digits, e.g. "03/07/2026". */
export function formatDateNumeric(date: Date | number | string): string {
  return formatDate(date, DATE_FORMATS.NUMERIC)
}

export function formatDateTime(date: Date | number | string): string {
  return formatDate(date, DATE_FORMATS.WITH_TIME)
}

export interface CurrencyFormatOptions {
  symbol?: string
  decimals?: number
  useGrouping?: boolean
  alwaysShowDecimals?: boolean
}

const DEFAULT_CURRENCY_OPTIONS: Required<CurrencyFormatOptions> = {
  symbol: CURRENCY.SYMBOL,
  decimals: CURRENCY.DECIMALS,
  useGrouping: CURRENCY.USE_GROUPING,
  alwaysShowDecimals: true,
}

const numberFormatterCache = new Map<string, Intl.NumberFormat>()

function getNumberFormatter(
  locale: string,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
  useGrouping: boolean,
): Intl.NumberFormat {
  const key = `${locale}|${minimumFractionDigits}|${maximumFractionDigits}|${useGrouping ? 1 : 0}`
  let formatter = numberFormatterCache.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
      useGrouping,
    })
    numberFormatterCache.set(key, formatter)
  }
  return formatter
}

const compactNumberFormatter = new Intl.NumberFormat(DATE_FORMATS.LOCALE, {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
})

export function formatCurrency(
  amount: number | string,
  options: CurrencyFormatOptions = {},
): string {
  const opts = { ...DEFAULT_CURRENCY_OPTIONS, ...options }

  const numAmount = typeof amount === 'string' ? Number.parseFloat(amount) : amount

  if (Number.isNaN(numAmount)) {
    return NUMBER_DISPLAY.FALLBACK_CURRENCY
  }

  const minimumFractionDigits = opts.alwaysShowDecimals ? opts.decimals : 0
  const formatter = getNumberFormatter(
    DATE_FORMATS.LOCALE,
    minimumFractionDigits,
    opts.decimals,
    opts.useGrouping,
  )
  const formatted = formatter.format(numAmount)

  return `${opts.symbol}${formatted}`
}

export function formatUSD(amount: number | string): string {
  return formatCurrency(amount, { symbol: CURRENCY.SYMBOL, decimals: CURRENCY.DECIMALS })
}

export function formatAmount(amount: number | string, maxDecimals: number = 2): string {
  const numAmount = typeof amount === 'string' ? Number.parseFloat(amount) : amount

  if (Number.isNaN(numAmount)) {
    return NUMBER_DISPLAY.FALLBACK_VALUE
  }

  if (Number.isInteger(numAmount)) {
    return getNumberFormatter(DATE_FORMATS.LOCALE, 0, 0, true).format(numAmount)
  }

  return getNumberFormatter(DATE_FORMATS.LOCALE, 0, maxDecimals, true).format(numAmount)
}

export function formatPercentage(value: number, decimals: number = CURRENCY.DECIMALS): string {
  if (Number.isNaN(value)) {
    return NUMBER_DISPLAY.FALLBACK_PERCENTAGE
  }

  return `${value.toFixed(decimals)}%`
}

export function formatCompactNumber(value: number): string {
  if (Number.isNaN(value)) {
    return NUMBER_DISPLAY.FALLBACK_VALUE
  }

  return compactNumberFormatter.format(value)
}

export function truncateAddress(
  address: string | null | undefined,
  prefixLength: number = ADDRESS_TRUNCATION.PREFIX_LENGTH,
  suffixLength: number = ADDRESS_TRUNCATION.SUFFIX_LENGTH,
): string {
  if (address == null || address === '') {
    return ''
  }

  const minLength = prefixLength + suffixLength + 3

  if (address.length <= minLength) {
    return address
  }

  return `${address.slice(0, prefixLength)}...${address.slice(-suffixLength)}`
}

export const truncateHash = truncateAddress

export function getTransactionUrl(hash: string): string {
  if (!hash) {
    return ''
  }
  return `${BASESCAN.TX_URL}${hash}`
}

export function getAddressUrl(address: string): string {
  if (!address) {
    return ''
  }
  return `${BASESCAN.ADDRESS_URL}${address}`
}

export function capitalize(str: string): string {
  if (!str) {
    return ''
  }
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`)
}

export function formatCount(count: number, singular: string, plural?: string): string {
  return `${count} ${pluralize(count, singular, plural)}`
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return '0 B'
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${units[i]}`
}

export function formatBigIntUnits(value: bigint, decimals: number): string {
  if (decimals < 0) {
    throw new Error('Decimals must be non-negative')
  }

  const divisor = 10n ** BigInt(decimals)
  const integerPart = value / divisor
  const fractionalPart = value % divisor

  const isNegative = value < 0n
  const absIntegerPart = isNegative ? -integerPart : integerPart
  const absFractionalPart = isNegative ? -fractionalPart : fractionalPart

  if (decimals === 0) {
    return absIntegerPart.toString()
  }

  const fractionalStr = absFractionalPart.toString().padStart(decimals, '0')

  const trimmedFractional = fractionalStr.replace(/0+$/, '') || '0'

  const sign = isNegative ? '-' : ''

  if (trimmedFractional === '0') {
    return `${sign}${absIntegerPart}`
  }

  return `${sign}${absIntegerPart}.${trimmedFractional}`
}

export function bigIntToNumber(value: bigint, decimals: number): number {
  return Number(formatBigIntUnits(value, decimals))
}

export function isValidAddress(address: unknown): address is `0x${string}` {
  if (typeof address !== 'string') {
    return false
  }

  return /^0x[a-fA-F0-9]{40}$/.test(address)
}
